import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, formatRM, formatDate, jobPic } from "@/lib/api";
import { JobItemsList } from "@/components/OrderItems";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";

export default function JobSheet() {
  const { id } = useParams();
  const [job, setJob] = useState(null);

  useEffect(() => { api.get(`/jobs/${id}`).then((r) => setJob(r.data.job)); }, [id]);

  if (!job) return <div className="p-8 text-muted-foreground">Loading...</div>;
  const url = `${window.location.origin}/scan/${job.id}`;
  const paid = (job.deposit_paid || 0) + (job.balance_paid || 0);

  return (
    <div className="min-h-screen bg-white text-black" data-testid="job-sheet-page">
      <div className="print:hidden flex gap-2 p-4 border-b bg-neutral-100">
        <Button variant="outline" size="sm" onClick={() => window.history.back()} data-testid="job-sheet-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={() => window.print()} data-testid="job-sheet-print"><Printer className="w-4 h-4 mr-1" /> Print</Button>
      </div>

      <div className="max-w-[210mm] mx-auto p-10 space-y-6">
        <div className="flex items-start justify-between border-b-4 border-black pb-4">
          <div>
            <img src="/rozfa-logo.jpg" alt="Rozfa Print Ops" className="h-10 w-auto mb-2" />
            <div className="text-xs font-mono uppercase tracking-widest">Job Sheet</div>
            <h1 className="text-4xl font-mono font-black mt-1" data-testid="job-sheet-number">{job.job_number}</h1>
            <div className="mt-2 inline-block border-2 border-black px-2 py-0.5 text-sm font-bold uppercase">{job.status}</div>
            {job.priority === "urgent" && <span className="ml-2 inline-block bg-black text-white px-2 py-0.5 text-sm font-bold uppercase">URGENT</span>}
          </div>
          <div className="text-center">
            <QRCodeSVG value={url} size={140} level="H" includeMargin data-testid="job-sheet-qr" />
            <div className="text-[10px] font-mono mt-1">Scan with phone camera to open job</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 text-sm">
          <section>
            <h2 className="font-bold uppercase text-xs tracking-wider border-b border-black mb-2">Customer</h2>
            <div className="font-semibold text-base">{job.customer_name}</div>
            <div>{job.customer_phone || "-"}</div>
            <div>{job.customer_email || "-"}</div>
          </section>
          <section>
            <h2 className="font-bold uppercase text-xs tracking-wider border-b border-black mb-2">Schedule</h2>
            <Row k="Deadline" v={formatDate(job.deadline)} />
            <Row k="Opened" v={formatDate(job.created_at)} />
            <Row k="PIC" v={jobPic(job)} />
            <Row k="Source" v={job.source || "-"} />
          </section>
        </div>

        <section className="text-sm">
          <h2 className="font-bold uppercase text-xs tracking-wider border-b border-black mb-2">Specifications</h2>
          {job.items?.length > 1 ? <JobItemsList job={job} /> : <>
          <Row k="Product" v={job.product} />
          <Row k="Material" v={job.material || "-"} />
          <Row k="Finishing" v={job.finishing || "-"} />
          <Row k="Dimensions" v={job.dimensions || "-"} /></>}
          <Row k="Quantity" v={job.quantity} />
          {job.notes && <Row k="Notes" v={job.notes} />}
        </section>

        {job.total_price !== undefined && <section className="text-sm">
          <h2 className="font-bold uppercase text-xs tracking-wider border-b border-black mb-2">Payment</h2>
          <Row k="Total" v={formatRM(job.total_price)} />
          <Row k="Paid" v={formatRM(paid)} />
          <Row k="Balance" v={formatRM((job.total_price || 0) - paid)} />
        </section>}

        <section className="grid grid-cols-4 gap-3 pt-4 text-xs">
          {["Design", "Print", "Finishing", "Packing"].map((s) => (
            <div key={s} className="border border-black h-20 p-1 font-bold uppercase">{s}<div className="text-[9px] font-normal mt-1">Signature / Date</div></div>
          ))}
        </section>
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between py-0.5 border-b border-dotted border-neutral-400">
    <span className="text-neutral-600">{k}</span>
    <span className="font-medium text-right">{v}</span>
  </div>
);
